import React, { useEffect, useMemo, useState } from 'react';
import { useDropzone, FileWithPath } from 'react-dropzone';
import { getBaseUrl } from '../services/api';

interface Props {
  label: string;
  existingPath?: string | null;
  file?: File | null;
  onFileChange?: (file: File | null) => void;
  required?: boolean;
  divClass?: string;
  dropzoneClass?: string; // allows controlling inner dropzone layout
  maxSizeMB?: number;
  previewClass?: string;
  maxPreviewHeight?: string; // CSS value for inline max-height (e.g. '200px' or '100%')
  maxPreviewWidth?: string; // CSS value for inline max-width
}

const ImageUpload: React.FC<Props> = ({
  label,
  existingPath,
  file: fileProp,
  onFileChange,
  required = false,
  divClass = '',
  dropzoneClass = 'border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary-500 relative h-full overflow-hidden',
  maxSizeMB = 5,
  previewClass = 'object-contain h-full w-full rounded-md max-h-full max-w-full',
  maxPreviewHeight,
  maxPreviewWidth,
}) => {
  const [internalFile, setInternalFile] = useState<File | null>(null);
  const file = fileProp !== undefined ? fileProp : internalFile;

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] },
    maxFiles: 1,
    maxSize: maxSizeMB * 1024 * 1024,
    onDrop: (accepted: FileWithPath[]) => {
      const f = accepted?.[0] ?? null;
      if (onFileChange) onFileChange(f);
      else setInternalFile(f);
    },
  });

  // preview URL
  const previewUrl = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    if (existingPath) return existingPath.startsWith('http') ? existingPath : `${getBaseUrl()}${existingPath}`;
    return null;
  }, [file, existingPath]);

  useEffect(() => {
    return () => {
      if (file) {
        try { URL.revokeObjectURL((file as any).__preview || ''); } catch (e) {}
      }
    };
    // we intentionally don't include previewUrl to avoid revoking external URLs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const removeFile = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (onFileChange) onFileChange(null);
    else setInternalFile(null);
  };

  return (
    <div className={divClass}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div
        {...getRootProps()}
        className={dropzoneClass}
      >
        <input {...getInputProps()} />
        {previewUrl ? (
          <div className="relative w-full h-full">
            {file && (
              <button
                type="button"
                onClick={removeFile}
                className="absolute top-2 right-2 z-10 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 focus:outline-none"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <img
              src={previewUrl}
              alt={label}
              className={`${previewClass}`}
              style={{ maxHeight: maxPreviewHeight || '100%', maxWidth: maxPreviewWidth || '100%' }}
            />
          </div>
        ) : (
          <div className="space-y-2 flex flex-col items-center justify-center h-full">
            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p className="text-sm text-gray-600">Drag & drop an image here, or click to select and upload {label}</p>
            {/* <p className="text-xs text-gray-500">PNG, JPG up to {maxSizeMB}MB</p> */}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;
